import React, { useState } from "react";
import { Pagination } from "@sscs/types";
import { DataTable, DataTableColumn, NLinesShowMore, Table } from "@sscs/ui";
import { calculatePaginationOffset, getQueryStatus } from "@sscs/utils";
import { useCheckPaginationPage } from "Hooks";
import { ExportJob } from "Types";
import { useGetAuditExportsQuery } from "../../services/audit.api";
import { ExportJobsResponse } from "../../types";
import ExportJobActions from "./ExportJobActions";
import ExportJobDownloadLink from "./ExportJobDownloadLink";

const COLUMNS: DataTableColumn[] = [
  { id: "name", label: "Name" },
  { id: "ndjson", label: "NDJSON", width: "250px" },
  { id: "cef", label: "CEF", width: "250px" },
  { id: "actions", label: "Actions", width: "100px" },
];

const ExportsTable = () => {
  const [pagination, setPagination] = useState<Pagination>({
    currentPage: 1,
    itemsPerPage: 10,
  });

  const handlePaginationChange = (pagination: Pagination) => {
    setPagination(pagination);
  };

  const {
    data: exportJobs = {
      match_count: 0,
      jobs: [],
    } satisfies ExportJobsResponse,
    isFetching,
    isLoading,
    isError,
    error,
  } = useGetAuditExportsQuery({
    limit: pagination.itemsPerPage,
    offset: calculatePaginationOffset(pagination),
  });

  useCheckPaginationPage({
    matchCount: exportJobs.match_count,
    itemLength: exportJobs.jobs.length,
    currentPage: pagination.currentPage,
    itemsPerPage: pagination.itemsPerPage,
    handlePaginationChange: setPagination,
  });

  return (
    <DataTable
      columns={COLUMNS}
      title="Exports"
      titleDetails={[
        "Exports are automatically removed once their download link expires.",
      ]}
      chrome="card"
      empty={{ subject: "exports" }}
      pagination={{
        itemCount: exportJobs.match_count,
        pagination,
        onChange: handlePaginationChange,
      }}
      status={getQueryStatus({
        isFetching,
        isLoading,
        isError,
        error,
        data: exportJobs,
      })}
    >
      {exportJobs.jobs.map((job: ExportJob, rowIndex) => (
        <Table.Row key={job.id}>
          <Table.Cell>
            <NLinesShowMore value={job.name} numberOfLines={2} />
          </Table.Cell>
          <Table.Cell>
            <ExportJobDownloadLink
              label={"Download NDJSON"}
              status={job.ndjson_status}
              download_url={job.ndjson_presigned_url}
              expires_at={job.ndjson_expires_at}
            />
          </Table.Cell>
          <Table.Cell>
            <ExportJobDownloadLink
              label={"Download CEF"}
              status={job.cef_status}
              download_url={job.cef_presigned_url}
              expires_at={job.cef_expires_at}
            />
          </Table.Cell>
          <Table.Cell>
            <ExportJobActions exportJob={job} rowIndex={rowIndex} />
          </Table.Cell>
        </Table.Row>
      ))}
    </DataTable>
  );
};

export default ExportsTable;
